import { Injectable, signal } from '@angular/core';
import { SCHOOL_LOGO_MAP } from '../core/config/school-logos.config';

/**
 * Portal UI Service
 * Manages global UI state for the portal layout, such as page titles and statistics.
 */
@Injectable({
    providedIn: 'root',
})
export class PortalUiService {
    /**
     * Current page level statistics (e.g., total result count)
     */
    readonly pageCount = signal<number | null>(null);

    /**
     * Current font size scale (-3 to +3)
     */
    readonly fontSizeScale = signal<number>(
        parseInt(localStorage.getItem('portal-font-size-scale') || '0', 10)
    );

    /**
     * Set the current page count to be displayed in the header
     */
    setPageCount(count: number | null): void {
        this.pageCount.set(count);
    }

    /**
     * Set the font size scale and persist to localStorage
     */
    setFontSizeScale(scale: number): void {
        const clampedScale = Math.max(-3, Math.min(3, scale));
        this.fontSizeScale.set(clampedScale);
        localStorage.setItem('portal-font-size-scale', clampedScale.toString());
    }

    /**
     * Currently active school logos to display centered in the header banner.
     * If null: standard continuous carousel with all school logos is displayed.
     * If populated: displays the parent Vantage logo + specific school logo(s) centered on the page without scrolling.
     */
    readonly activeSchoolLogos = signal<string[] | null>(null);

    /**
     * Set active schools by shortname(s), name(s), or logo paths.
     * Automatically includes parent Vantage logo alongside the school logo(s).
     */
    setActiveSchools(schools: string[] | string | null | undefined): void {
        if (!schools) {
            this.activeSchoolLogos.set(null);
            return;
        }

        const list = Array.isArray(schools) ? schools : [schools];
        const logos: string[] = [];

        // Always include parent Vantage logo first
        logos.push('/logo_vantage.svg');

        for (const item of list) {
            if (!item) continue;
            const upper = item.toUpperCase().trim();
            if (upper.endsWith('.PNG') || upper.endsWith('.SVG')) {
                if (!logos.includes(item)) logos.push(item);
            } else {
                const entry = SCHOOL_LOGO_MAP[upper];
                const logoFile = entry ? entry.logo : this.getLogoForSchoolShortname(upper);
                if (logoFile && !logos.includes(logoFile)) {
                    logos.push(logoFile);
                }
            }
        }

        this.activeSchoolLogos.set(logos.length > 0 ? logos : null);
    }

    private getLogoForSchoolShortname(shortname: string): string | null {
        const map: Record<string, string> = {
            HSO: '/hso_logo.png',
            BVS: '/bvs_logo.png',
            SMA: '/sma_logo.png',
            IST: '/ist_logo.png',
            PRA: '/pra_logo.png',
            APA: '/apa_logo.png',
            MBS: '/mbs_logo.png',
            NOV: '/nov_logo.png',
            OSD: '/osd_logo.png',
            BOF: '/logo_vantage.svg',
            BIO: '/logo_vantage.svg',
            SOA: '/soa_logo.svg',
            VAN: '/logo_vantage.svg',
        };
        return map[shortname] || null;
    }

    /**
     * Reset UI state (call on navigation)
     */
    reset(): void {
        this.pageCount.set(null);
    }
}
